import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useTransactions } from '../hooks/useTransactions'
import { TransactionResponse } from '../services/transactionService/transaction'
import { useDebounce } from '../hooks/useDebounce'
import { useTransactionChartHook } from '../hooks/useTransactionChartHook'
import { TransactionChartResponse } from '../services/transactionService/transactionChart'
import { useLocation } from 'react-router-dom'

interface FiltersType {
  sellerIds?: string
  customerIds?: string
  teamIds?: string
  startDate?: string
  endDate?: string
  status?: string
}

interface TransactionContextValue {
  setSkip: (value: number) => void
  transaction: {
    isLoading: boolean
    isFetching: boolean
    data: TransactionResponse | undefined
    fetchNextPage?: () => void
    hasNextPage?: boolean
    isFetchingNextPage: boolean
  }
  setSearchValue: React.Dispatch<React.SetStateAction<string | undefined>>
  debouncedSearchValue?: string
  transactionChart: {
    isLoading: boolean;
    isFetching: boolean;
    data: TransactionChartResponse | undefined;
  };
  setFilterParams: (value: FiltersType) => void;
  refetch: () => void;
}
export const TransactionContext = createContext({} as TransactionContextValue)

export function TransactionProvider({ children }: { children: ReactNode }) {
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)
  const debouncedSearchValue = useDebounce(500, searchValue)

  const location = useLocation()

  const interval = ['/transacoes'].includes(location.pathname)
    ? 5 * 60 * 1000
    : false

  const setSkip = (value: number) => {
    setParams((state) => ({ ...state, skip: value }))
  }

  const setFilterParams = (value: FiltersType) => {
    setParams(value)
    setParamsChart(value)
  }

  const { refetch, setParams, ...transaction } = useTransactions({
    interval,
    useInfiniteScroll: true,
  })

  const { setParams: setParamsChart, ...transactionChart } =
    useTransactionChartHook({ interval })

  const setSearch = useCallback(() => {
    setParams((state) => ({ ...state, search: debouncedSearchValue }))
  }, [debouncedSearchValue, setParams])

  const resetSearch = useCallback(() => {
    setSearchValue(undefined)
    setParams((state) => ({ ...state, search: undefined }))
  }, [setParams])

  useEffect(() => {
    setSearch()
  }, [setSearch])

  useEffect(() => {
    resetSearch()
  }, [location.pathname, resetSearch])

  return (
    <TransactionContext.Provider
      value={{
        setSkip,
        transaction,
        setSearchValue,
        debouncedSearchValue,
        transactionChart,
        setFilterParams,
        refetch: refetch || (() => { }),
      }}
    >
      {children}
    </TransactionContext.Provider>
  )
}
